import {
  getActiviteId,
  captureDetailHasRequiredMasses,
  PECHE_QUANTITE_MASSE_PAIRS,
  toNumber,
  toDecimal,
  estReponseOuiNon,
  obtenirIdsUniques,
  obtenirNombreRestantsBloc,
  trouverDetailParId,
  obtenirLieuxDisponibles,
  aPlusDeLieuxDisponibles,
  blocLieuEstComplet,
  indexBlocAutreLieu,
} from './fermer-enregistrement-capture.js';

export function resolveEspeceDescription(espece) {
  return espece.description
    || espece.Description
    || espece.descriptionEspece
    || espece.noEspece
    || espece.no_espece
    || espece.NoEspece
    || '';
}

export function buildCaptureDetailsFromEspeces(especes) {
  return (especes || []).map(createEmptyCaptureDetail);
}

export function findActivitePeche(activites) {
  return activites.find(a => a.peche === true || a.peche === 1);
}

export function findPecheEnregistrementDetail(enregistrement, activites) {
  const idActivitesPeche = activites
    .filter(a => a.peche === true || a.peche === 1)
    .map(a => getActiviteId(a));

  return (enregistrement?.enregistrementDetails || []).find(d => idActivitesPeche.includes(d.idActivite));
}

export function createEmptyCaptureDetail(espece) {
  const idEspece = espece.idEspece || espece.IdEspece || espece.id || espece.Id;
  return {
    idEspece,
    descriptionEspece: resolveEspeceDescription(espece),
    completQuantite: '',
    completMasse: '',
    eviscereQuantite: '',
    eviscereMasse: '',
    eteteQuantite: '',
    eteteMasse: '',
    consommeQuantite: '',
    nonPeseeQuantite: '',
    remisAEau: '',
    longueur: '',
    remarques: '',
    aConsommeEspece: null,
    aRemisAEau: null,
    aPese: null,
    aNonPese: null,
  };
}

export function aPlusieursEspecesCapture(capturesDetails) {
  return obtenirIdsEspecesCapture(capturesDetails).length > 1;
}

export function obtenirIdsEspecesCapture(capturesDetails) {
  return obtenirIdsUniques(capturesDetails, detail => detail.idEspece || '');
}

export function obtenirNombreEspecesRestantes(block) {
  return obtenirNombreRestantsBloc(block, detail => detail.idEspece || '');
}

export function obtenirDetailEspeceActive(block) {
  return trouverDetailParId(block.capturesDetails, block.activeEspeceId, detail => detail.idEspece || '');
}

export function questionsEspeceActiveCompletees(block) {
  return questionsDetailEspeceCompletees(obtenirDetailEspeceActive(block));
}

export function quantiteConsommeCompletee(detail) {
  return detail.aConsommeEspece !== true || Number(detail.consommeQuantite) > 0;
}

export function quantiteRemisAEauCompletee(detail) {
  return detail.aRemisAEau !== true || Number(detail.remisAEau) > 0;
}

export function quantitesPeseCompletees(detail) {
  if (detail.aPese !== true) {
    return true;
  }
  const aUneQuantite = PECHE_QUANTITE_MASSE_PAIRS.some(
    ([quantiteField]) => Number(detail[quantiteField]) > 0
  );
  return aUneQuantite && captureDetailHasRequiredMasses(detail);
}

export function quantiteNonPeseCompletee(detail) {
  return detail.aNonPese !== true || Number(detail.nonPeseeQuantite) > 0;
}

export function questionsDetailEspeceCompletees(detail) {
  if (!detail) {
    return false;
  }
  if (!estReponseOuiNon(detail.aConsommeEspece) || !quantiteConsommeCompletee(detail)) {
    return false;
  }
  if (!estReponseOuiNon(detail.aRemisAEau) || !quantiteRemisAEauCompletee(detail)) {
    return false;
  }
  if (!estReponseOuiNon(detail.aPese) || !quantitesPeseCompletees(detail)) {
    return false;
  }
  if (!estReponseOuiNon(detail.aNonPese) || !quantiteNonPeseCompletee(detail)) {
    return false;
  }
  return true;
}

function obtenirDetailEspeceParId(block, especeId) {
  return trouverDetailParId(block.capturesDetails, especeId, detail => detail.idEspece || '');
}

export function fluxEspecesCaptureComplete(block) {
  const rounds = block.especeRounds || [];
  for (const round of rounds) {
    if (!questionsDetailEspeceCompletees(obtenirDetailEspeceParId(block, round.speciesId))) {
      return false;
    }
  }
  const activeId = String(block.activeEspeceId || '');
  if (activeId) {
    return questionsEspeceActiveCompletees(block);
  }
  return rounds.length > 0;
}

function definirReponseRound(especeRounds, roundIndex, valeur) {
  return especeRounds.map((round, i) =>
    i === roundIndex ? { ...round, autreEspece: valeur } : round
  );
}

export function verrouillerEspeceActiveSiCompletee(block) {
  if (block.aEuDesPrises !== true) {
    return block;
  }
  if (!aPlusieursEspecesCapture(block.capturesDetails)) {
    return block;
  }
  const idActif = String(block.activeEspeceId || '');
  if (!idActif || !questionsEspeceActiveCompletees(block)) {
    return block;
  }

  const especeRounds = [...(block.especeRounds || [])];
  if (!especeRounds.some(round => String(round.speciesId) === idActif)) {
    especeRounds.push({ speciesId: idActif, autreEspece: null });
  }

  const blocSuivant = {
    ...block,
    especeRounds,
    activeEspeceId: '',
    autreEspece: null,
  };

  if (obtenirNombreEspecesRestantes(blocSuivant) <= 0) {
    return {
      ...blocSuivant,
      especeRounds: definirReponseRound(especeRounds, especeRounds.length - 1, false),
      autreEspece: false,
    };
  }
  return blocSuivant;
}

export function montrerQuestionAutreEspeceRound(block, roundIndex) {
  if (block.aEuDesPrises !== true) {
    return false;
  }
  if (!aPlusieursEspecesCapture(block.capturesDetails)) {
    return false;
  }
  const rounds = block.especeRounds || [];
  if (roundIndex < 0 || roundIndex >= rounds.length) {
    return false;
  }
  if (roundIndex < rounds.length - 1) {
    return true;
  }
  return obtenirNombreEspecesRestantes(block) > 0 || Boolean(block.activeEspeceId);
}

export function obtenirReponseAutreEspeceRound(block, roundIndex) {
  const round = (block.especeRounds || [])[roundIndex];
  if (!round || round.autreEspece === undefined) {
    return null;
  }
  return round.autreEspece;
}

export function doitAfficherSaisieEspeceActive(block) {
  if (block.aEuDesPrises !== true) {
    return false;
  }
  if (!aPlusieursEspecesCapture(block.capturesDetails)) {
    return true;
  }
  if (block.activeEspeceId) {
    return true;
  }
  if ((block.especeRounds || []).length === 0) {
    return true;
  }
  if (obtenirNombreEspecesRestantes(block) <= 0) {
    return false;
  }
  return block.autreEspece === true;
}

export function montrerListeDeroulanteEspece(block) {
  return doitAfficherSaisieEspeceActive(block);
}

export function afficherListeDeroulanteEspeceActive(block) {
  return doitAfficherSaisieEspeceActive(block);
}

export function obtenirEspeceActiveInitiale(capturesDetails) {
  const ids = obtenirIdsEspecesCapture(capturesDetails);
  return ids.length === 1 ? ids[0] : '';
}

export function estQuestionAutreEspeceResolue(block) {
  if (block.aEuDesPrises !== true) {
    return true;
  }
  if (!aPlusieursEspecesCapture(block.capturesDetails)) {
    return questionsEspeceActiveCompletees(block);
  }
  if (block.activeEspeceId) {
    return false;
  }
  if ((block.especeRounds || []).length === 0) {
    return false;
  }
  if (block.autreEspece === false || obtenirNombreEspecesRestantes(block) <= 0) {
    return fluxEspecesCaptureComplete(block);
  }
  return false;
}

export function appliquerChangementEspeceActive(block, especeId) {
  const nouvelId = String(especeId || '');
  const idsRounds = (block.especeRounds || []).map(round => String(round.speciesId));
  const idsAConserver = nouvelId ? [...idsRounds, nouvelId] : idsRounds;

  return {
    ...block,
    activeEspeceId: nouvelId,
    capturesDetails: reinitialiserCapturesDetailsExcept(block.capturesDetails, idsAConserver),
  };
}

export function reinitialiserDetailCaptureEspece(detail) {
  const idEspece = detail.idEspece || '';
  return createEmptyCaptureDetail({
    idEspece,
    descriptionEspece: detail.descriptionEspece,
  });
}

export function reinitialiserCapturesDetailsExcept(capturesDetails, idsAConserver) {
  const conserver = new Set((idsAConserver || []).map(String));
  return (capturesDetails || []).map(detail => {
    const id = String(detail.idEspece || '');
    if (conserver.has(id)) {
      return detail;
    }
    return reinitialiserDetailCaptureEspece(detail);
  });
}

export function appliquerAutreEspeceOui(block, roundIndex) {
  const blocVerrouille = verrouillerEspeceActiveSiCompletee(block);

  if (blocVerrouille.activeEspeceId) {
    return block;
  }

  const rounds = blocVerrouille.especeRounds || [];
  const index = Number.isInteger(roundIndex) ? roundIndex : rounds.length - 1;
  if (index < 0 || index >= rounds.length) {
    return block;
  }

  if (index < rounds.length - 1) {
    return {
      ...blocVerrouille,
      especeRounds: definirReponseRound(rounds, index, true),
    };
  }

  if (obtenirNombreEspecesRestantes(blocVerrouille) <= 0) {
    return {
      ...blocVerrouille,
      especeRounds: definirReponseRound(rounds, index, false),
      autreEspece: false,
    };
  }

  return {
    ...blocVerrouille,
    especeRounds: definirReponseRound(rounds, index, true),
    autreEspece: true,
    autrePointEau: null,
  };
}

export function appliquerAutreEspeceNon(block, roundIndex) {
  const rounds = block.especeRounds || [];
  const index = Number.isInteger(roundIndex) ? roundIndex : rounds.length - 1;

  if (index < 0 || index >= rounds.length) {
    return {
      ...block,
      autreEspece: false,
      activeEspeceId: '',
    };
  }

  const especeRounds = definirReponseRound(rounds.slice(0, index + 1), index, false);
  const idsRounds = especeRounds.map(round => String(round.speciesId));

  return {
    ...block,
    autreEspece: false,
    activeEspeceId: '',
    especeRounds,
    capturesDetails: reinitialiserCapturesDetailsExcept(block.capturesDetails, idsRounds),
  };
}

let pointEauBlockCounter = 0;

export function createEmptyPointEauBlock() {
  pointEauBlockCounter += 1;
  return {
    id: `pe-${pointEauBlockCounter}`,
    idPointEau: '',
    nombrePersonnes: '',
    nombreJours: '',
    autrePointEau: null,
    autreEspece: null,
    especeRounds: [],
    activeEspeceId: '',
    aEuDesPrises: null,
    capturesDetails: [],
  };
}

export function getPointsEauxDisponibles(allPointsEaux, pointsEau, index) {
  return obtenirLieuxDisponibles(allPointsEaux, pointsEau, index, 'idPointEau');
}

export function hasMorePointsEauxDisponibles(allPointsEaux, pointsEau) {
  return aPlusDeLieuxDisponibles(allPointsEaux, pointsEau, 'idPointEau');
}

export function shouldRenderPointEauBlock(block, index, allPointsEaux, pointsEau) {
  if (index === 0) {
    return true;
  }
  if (block.idPointEau) {
    return true;
  }
  const previousBlock = pointsEau[index - 1];
  if (!previousBlock || previousBlock.autrePointEau !== true) {
    return false;
  }
  return getPointsEauxDisponibles(allPointsEaux, pointsEau, index).length > 0;
}

export function pruneOrphanPointEauBlocks(pointsEau, allPointsEaux) {
  return pointsEau.filter((block, index) =>
    shouldRenderPointEauBlock(block, index, allPointsEaux, pointsEau)
  );
}

export function estComplete(block) {
  return blocLieuEstComplet(block, 'idPointEau');
}

export function getAutrePointEauBlockIndex(pointsEau) {
  return indexBlocAutreLieu(pointsEau, 'idPointEau');
}

export function afficherAutrePointEau(index, pointsEau, allPointsEaux) {
  const block = pointsEau[index];
  if (!estComplete(block)) {
    return false;
  }
  if (block.aEuDesPrises === null) {
    return false;
  }
  if (!estQuestionAutreEspeceResolue(block)) {
    return false;
  }
  if (block.autrePointEau !== null && block.autrePointEau !== undefined) {
    return true;
  }
  if (!hasMorePointsEauxDisponibles(allPointsEaux, pointsEau)) {
    return false;
  }
  return index === getAutrePointEauBlockIndex(pointsEau);
}

export function formEstComplete({ dateIsSelected, heureIsSelected, aPeche, pointsEau, allPointsEaux = [] }) {
  if (!dateIsSelected || !heureIsSelected || aPeche === null) {
    return false;
  }

  if (aPeche === false) {
    return true;
  }

  if (!pointsEau.length) {
    return false;
  }

  for (let i = 0; i < pointsEau.length; i++) {
    const block = pointsEau[i];
    if (!block.idPointEau) {
      return false;
    }
    if (!block.nombrePersonnes || Number(block.nombrePersonnes) <= 0) {
      return false;
    }
    if (!block.nombreJours || Number(block.nombreJours) <= 0) {
      return false;
    }

    if (block.aEuDesPrises === null) {
      return false;
    }
    if (block.aEuDesPrises === true) {
      const details = block.capturesDetails || [];
      if (!details.every(captureDetailHasRequiredMasses)) {
        return false;
      }
      if (!estQuestionAutreEspeceResolue(block)) {
        return false;
      }
    }
  }

  const autrePointEauBlockIndex = getAutrePointEauBlockIndex(pointsEau);
  if (autrePointEauBlockIndex >= 0 && hasMorePointsEauxDisponibles(allPointsEaux, pointsEau)) {
    const autreBlock = pointsEau[autrePointEauBlockIndex];
    if (autreBlock.autrePointEau !== false) {
      return false;
    }
  }

  return true;
}

function mapCaptureDetailFields(detail) {
  return {
    IdEspece: detail.idEspece,
    CompletQuantite: toNumber(detail.completQuantite),
    CompletMasse: toDecimal(detail.completMasse),
    EviscereQuantite: toNumber(detail.eviscereQuantite),
    EviscereMasse: toDecimal(detail.eviscereMasse),
    EteteQuantite: toNumber(detail.eteteQuantite),
    EteteMasse: toDecimal(detail.eteteMasse),
    ConsommeQuantite: toNumber(detail.consommeQuantite),
    NonPeseeQuantite: toNumber(detail.nonPeseeQuantite),
    RemisAEau: toNumber(detail.remisAEau),
    Longueur: toDecimal(detail.longueur),
    Remarques: detail.remarques || '',
  };
}

export function buildFermetureCapturePayload({
  idProfile,
  dateFermetureISO,
  idEnregistrementEntete,
  idEnregistrementDetailPeche,
  aPeche,
  pointsEau,
}) {
  return {
    IdProfile: idProfile,
    DateFermeture: dateFermetureISO,
    IdEnregistrementEntete: idEnregistrementEntete,
    ActivitesPeche: [{
      IdEnregistrementDetail: idEnregistrementDetailPeche,
      APeche: aPeche === true,
      PointsEau: aPeche === true
        ? (pointsEau || [])
          .filter(block => block.idPointEau)
          .map(block => ({
            IdPointEau: block.idPointEau,
            NombrePersonnes: toNumber(block.nombrePersonnes),
            NombreJours: toNumber(block.nombreJours),
            AEuDesPrises: block.aEuDesPrises === true,
            CapturesDetails: (block.capturesDetails || []).map(mapCaptureDetailFields),
          }))
        : [],
    }],
  };
}
